import _ from 'lodash'
import {commonFunctions} from 'common-types'
import {extractLayoutToNonCoveredRanges} from './layoutToNonCoveredRanges'
import {SelectorToCssMap, layoutDataItemToCss} from './layoutToCss'
import {setDetachedItemCssOverrides, clearAllDetachedItemsCssOverrides} from './detachedItemsOverrides'
import {withActions} from 'carmi-host-extensions/src'
import {itemLayoutTypes} from 'common-types/src/layoutData'

const measureBoundingRect = (compId, windowObject) => {
    const compBoundingClientRect = windowObject.document.getElementById(compId).getBoundingClientRect()

    return {
        left: compBoundingClientRect.left,
        top: compBoundingClientRect.top,
        width: compBoundingClientRect.width,
        height: compBoundingClientRect.height
    }
}

const getContainerDisplay = (compId, contentWrapperClass, windowObject) => {
    const containerNode = windowObject.document.getElementById(compId)
    const contentWrapper = containerNode.querySelector(`.${contentWrapperClass}`)
    const contentWrapperComputedStyle = windowObject.getComputedStyle(contentWrapper)

    return contentWrapperComputedStyle.display
}

const measureContainer = (compId, contentWrapperClass, overflowWrapperClass, windowObject) => {
    const containerNode = windowObject.document.getElementById(compId)
    const overflowWrapper = containerNode.querySelector(`.${overflowWrapperClass}`)
    const contentWrapper = containerNode.querySelector(`.${contentWrapperClass}`)
    const contentWrapperComputedStyle = windowObject.getComputedStyle(contentWrapper)
    const display = contentWrapperComputedStyle.display

    return _.assign({
        containerMeasures: {
            display
        }
    }, overflowWrapper && {
        overflowWrapper: {
            scrollWidth: overflowWrapper.scrollWidth,
            scrollHeight: overflowWrapper.scrollHeight
        }
    }, display === 'grid' && {
        gridMeasures: {
            gridTemplateRows: contentWrapperComputedStyle.gridTemplateRows,
            gridTemplateColumns: contentWrapperComputedStyle.gridTemplateColumns
        }
    })
}

export const name = 'ResponsiveStylesAspect'

export const defaultModel = {
    detachedItems: {},
    placeholdersOverrides: []
}

interface CssWithMedia {
    [mediaQuery: string]: SelectorToCssMap
}

export interface BreakpointsToCssWithMedia {
    [breakpointId: string]: CssWithMedia
}

const mergeCssWithMediaArray = (array: BreakpointsToCssWithMedia[]) => array.reduce(_.merge, {}) as BreakpointsToCssWithMedia

const extractHiddenToNonCoveredRanges = extractLayoutToNonCoveredRanges('ComponentLayout', 'hidden', v => v)
const extractAspectRatioToNonCoveredRanges = extractLayoutToNonCoveredRanges('ComponentLayout', 'height', v => v && v.type === 'aspectRatio')
const extractStickyToNonCoveredRanges = (type: string) => extractLayoutToNonCoveredRanges(type, 'position', v => v && v === 'sticky', ['top', 'bottom', 'left', 'right'])

const extractPropertiesToNonCoveredRanges = (layouts, bpOrder) => {
    const layoutsAfterHiddenExtraction = extractHiddenToNonCoveredRanges(layouts, bpOrder)
    let layoutsAfterStickyExtraction = layoutsAfterHiddenExtraction
    _.forEach(itemLayoutTypes, type => {
        layoutsAfterStickyExtraction = extractStickyToNonCoveredRanges(type)(layoutsAfterStickyExtraction, bpOrder)
    })
    return extractAspectRatioToNonCoveredRanges(layoutsAfterStickyExtraction, bpOrder)
}

const getContainerScroll = (windowObject, containerId, overflowWrapperClass) => {
    const containerElement = windowObject.document.getElementById(containerId)
    const overflowWrapper = containerElement.querySelector(`.${overflowWrapperClass}`)
    const {scrollTop: top = 0, scrollLeft: left = 0} = overflowWrapper || {}
    return {
        top,
        left
    }
}

const getCompsBoundingRect = (compIds, windowObject, parentsMap, containerClasses) => {
    const compsBoundingRects = {}

    const getCompsBoundingRectRecursive = currCompId => {
        if (compsBoundingRects[currCompId]) {
            return compsBoundingRects[currCompId]
        }

        const parentId = parentsMap[currCompId]
        const selfBoundingRect = measureBoundingRect(currCompId, windowObject)

        let currBoundingRect
        if (parentId) {
            const parentBoundingRect = getCompsBoundingRectRecursive(parentId)
            const overflowWrapperClass = containerClasses[parentId].layoutContainerOverflowWrapperClassName
            const containerScroll = getContainerScroll(windowObject, parentId, overflowWrapperClass)
            currBoundingRect = _.assign({
                offsetTop: containerScroll.top + selfBoundingRect.top - parentBoundingRect.top,
                offsetLeft: containerScroll.left + selfBoundingRect.left - parentBoundingRect.left
            }, selfBoundingRect)
        } else {
            currBoundingRect = _.assign({
                offsetTop: 0,
                offsetLeft: 0
            }, selfBoundingRect)
        }

        compsBoundingRects[currCompId] = currBoundingRect
        return currBoundingRect
    }

    _.forEach(compIds, getCompsBoundingRectRecursive)

    return compsBoundingRects
}

const getPlaceholderOverrides = (childrenToLock, ancestorsToLock, compsBoundingRects, ancestorsMeasuresForPlaceholders, containerClasses) => {
    const placeholderOverrides = []

    childrenToLock.forEach(compId => {
        const compBoundingRect = compsBoundingRects[compId]

        placeholderOverrides.push({
            selector: `#${compId}`,
            css: {
                position: 'absolute',
                left: `${compBoundingRect.offsetLeft}px`,
                top: `${compBoundingRect.offsetTop}px`,
                width: `${compBoundingRect.width}px`,
                height: `${compBoundingRect.height}px`,
                margin: '0'
            }
        })
    })

    ancestorsToLock.forEach(containerId => {
        const containerBoundingRect = compsBoundingRects[containerId]
        const measures = ancestorsMeasuresForPlaceholders[containerId]
        const contentWrapperClass = containerClasses[containerId].layoutContainerClassName

        const rootCss = {
            width: `${containerBoundingRect.width}px`,
            height: `${containerBoundingRect.height}px`
        }

        const contentWrapperCss = _.assign({},
            measures.containerMeasures.display === 'grid' &&
            {
                'grid-template-rows': measures.gridMeasures.gridTemplateRows,
                'grid-template-columns': measures.gridMeasures.gridTemplateColumns
            },
            _.has(measures, 'overflowWrapper') &&
            {
                width: `${measures.overflowWrapper.scrollWidth}px`,
                height: `${measures.overflowWrapper.scrollHeight}px`
            }
        )

        placeholderOverrides.push({
            selector: `#${containerId}`,
            css: rootCss
        })

        placeholderOverrides.push({
            selector: `#${containerId} .${contentWrapperClass}`,
            css: contentWrapperCss
        })
    })

    return placeholderOverrides
}

const startDetach = withActions((actions, {ancestorsMap, containerClasses, compsChildrenMap, parentsMap, windowObject}, compsDetachData) => {
    const {$runInBatch, setDetachedItems, setPlaceholdersOverrides} = actions
    const ancestorsToLock = _(compsDetachData)
        .keys()
        .flatMap(compId => ancestorsMap[compId])
        .uniq()
        .value()

    const isGridContainer = containerId => getContainerDisplay(containerId, containerClasses[containerId].layoutContainerClassName, windowObject) === 'grid'

    const isGridMap = _.reduce(ancestorsToLock, (result, containerId) => {
        result[containerId] = isGridContainer(containerId)

        return result
    }, {})

    const shouldLockChildren = containerId => !isGridMap[containerId]
    const shouldLockComp = compId => !_.has(compsDetachData, compId)

    const childrenToLock = _.flatMap(ancestorsToLock, containerId => shouldLockChildren(containerId) ?
        _.filter(compsChildrenMap[containerId], shouldLockComp) :
        []
    )

    const compsToLock = childrenToLock.concat(ancestorsToLock)

    const compsBoundingRects = getCompsBoundingRect(compsToLock, windowObject, parentsMap, containerClasses)

    const ancestorsMeasuresForPlaceholders = _.reduce(ancestorsToLock, (result, containerId) => {
        const contentWrapperClass = containerClasses[containerId].layoutContainerClassName
        const overflowWrapperClass = containerClasses[containerId].layoutContainerOverflowWrapperClassName

        result[containerId] = measureContainer(containerId, contentWrapperClass, overflowWrapperClass, windowObject)

        return result
    }, {})

    $runInBatch(() => {
        setPlaceholdersOverrides(getPlaceholderOverrides(childrenToLock, ancestorsToLock, compsBoundingRects, ancestorsMeasuresForPlaceholders, containerClasses))
        setDetachedItems(compsDetachData)
    })
})

const endDetach = withActions((actions, windowObject) => {
    const {$runInBatch, setDetachedItems, setPlaceholdersOverrides} = actions

    clearAllDetachedItemsCssOverrides(windowObject)
    $runInBatch(() => {
        setDetachedItems(defaultModel.detachedItems)
        setPlaceholdersOverrides(defaultModel.placeholdersOverrides)
    })
})

export const functionLibrary = {
    extractPropertiesToNonCoveredRanges,
    layoutDataItemToCss,
    mergeCssWithMediaArray,
    setDetachedItemCssOverrides,
    startDetach,
    endDetach
}

export type FunctionLibrary = typeof functionLibrary & commonFunctions
